import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import type { User } from '@openx/types/user';
import type { JSX, ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../Button/Button';
import { useRHFNestedFormContext } from '../Form/context/RHFNestedFormContext';

const StyledFormControl = styled(FormControl)`
  bottom: 0;
  left: 0;
  margin: 0;
  background-color: ${({ theme }) => theme.palette.background.boxLight};
  border-top: 1px solid ${({ theme }) => theme.palette.divider};
  padding: ${({ theme }) => theme.spacing(1.2, 3.8)};
  position: sticky;
  z-index: 5;
`;

export type FormActionsProps = {
  children?: ReactNode | ReactNode[];
  submitButtonLabel?: string;
  cancelButtonLabel?: string;
  disabled?: boolean;
  isSubmitting?: boolean;
  isSubmittingAllowed?: boolean;
  notAllowedMessage?: string;
  user?: User;
  onSubmit?: () => void;
  onCancel?: (() => void | Promise<void>) | false;
  openXAgentTools?: () => JSX.Element;
};

export const FormActions = ({
  children,
  submitButtonLabel = 'Save',
  cancelButtonLabel = 'Cancel',
  disabled,
  isSubmitting,
  isSubmittingAllowed = true,
  notAllowedMessage = '',
  onSubmit,
  onCancel,
  openXAgentTools,
}: FormActionsProps): ReactNode => {
  const {
    formConfig: { noActions },
  } = useRHFNestedFormContext();

  const navigate = useNavigate();
  const goBackMethod = (): Promise<void> | void => navigate(-1);
  const cancelMethod = onCancel || goBackMethod;

  if (noActions) {
    return null;
  }

  return (
    <StyledFormControl margin="normal" fullWidth>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: 'center',
        }}
      >
        {children ? (
          children
        ) : (
          <>
            <Button
              type="submit"
              color="primary"
              loading={isSubmitting}
              disabled={disabled}
              onClick={onSubmit}
              data-test="submit-button"
              allowed={isSubmittingAllowed}
              notAllowedMessage={notAllowedMessage}
            >
              {submitButtonLabel}
            </Button>

            {openXAgentTools?.()}

            {onCancel !== false && (
              <Button variant="text" onClick={cancelMethod} data-test="cancel-button">
                {cancelButtonLabel}
              </Button>
            )}
          </>
        )}
      </Stack>
    </StyledFormControl>
  );
};
